import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api, ApiError } from '../api/client';

/** The browser can create and use passkeys. */
export const passkeysSupported = (): boolean => browserSupportsWebAuthn();

/** Browser and password manager offer passkeys in the address field (conditional UI). */
export const passkeyAutofillSupported = (): Promise<boolean> => browserSupportsWebAuthnAutofill();

/** Ends a pending passkey prompt, e.g. the autofill of the login page when it closes. */
export const cancelPasskeyPrompt = (): void => WebAuthnAbortService.cancelCeremony();

/**
 * The person closed the prompt or another prompt replaced it. Browsers do not tell a refusal from
 * a cancel (privacy), so neither shows an error.
 */
export function isPasskeyCancelled(error: unknown): boolean {
  if (error instanceof WebAuthnError) {
    return (
      error.code === 'ERROR_CEREMONY_ABORTED' ||
      (error.cause as Error | undefined)?.name === 'NotAllowedError'
    );
  }
  return (
    error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/** The authenticator already holds a passkey for this account. */
export const isPasskeyKnown = (error: unknown): boolean =>
  error instanceof WebAuthnError && error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';

/**
 * Login with a passkey (ADR-0044). No address needed: the passkey names account and company.
 * `autofill` waits for the person to pick a passkey in the address field instead of opening a
 * prompt.
 */
export async function signInWithPasskey(autofill = false): Promise<void> {
  const optionsJSON =
    await api.post<PublicKeyCredentialRequestOptionsJSON>('/auth/passkey/challenge');
  const response = await startAuthentication({ optionsJSON, useBrowserAutofill: autofill });
  try {
    await api.post('/auth/passkey/sign-in', { response });
  } catch (error) {
    if (error instanceof ApiError && error.message === 'passkey-unknown') {
      forgetPasskey(optionsJSON.rpId, response.id);
    }
    throw error;
  }
}

/** Adds a passkey to the own account; the server asks for the password first. */
export async function addPasskey(password: string): Promise<{ id: string; name: string | null }> {
  const optionsJSON = await api.post<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/passkeys/challenge',
    { password },
  );
  const response = await startRegistration({ optionsJSON });
  return api.post('/auth/passkeys', { response });
}

type SignalUnknown = (options: { rpId: string; credentialId: string }) => Promise<void>;

/**
 * Tells the password manager that the app no longer knows this passkey (WebAuthn Signal API, e.g.
 * 1Password, Chrome). It then stops offering it. Browsers without the API ignore it.
 */
function forgetPasskey(rpId: string | undefined, credentialId: string): void {
  const signal = (PublicKeyCredential as unknown as { signalUnknownCredential?: SignalUnknown })
    .signalUnknownCredential;
  if (!signal || !rpId) return;
  void signal.call(PublicKeyCredential, { rpId, credentialId }).catch(() => undefined);
}
